import {ImageService} from './base-service'
import {encodeUrlPath} from '../responsive-image'
import {getSharedLibraryAssetUrl} from '../url'
import logger from '../logger-instance'

const FORMAT_EXTENSIONS = {
    'image/avif': 'avif',
    'image/webp': 'webp',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif'
}

const supportsTransparency = (format) => {
    const ext = format.replace('image/', '').toLowerCase()
    return ['avif', 'webp', 'png', 'gif'].includes(ext)
}

const getOriginalFormat = (url) => {
    if (!url) return 'jpg'

    const validExtensions = ['gif', 'jp2', 'jpg', 'jpeg', 'png', 'tiff', 'tif']

    // Check for existing sfrm parameter
    if (url.includes('?') || url.includes('&')) {
        const queryString = url.includes('?') ? url.split('?')[1] : ''
        if (queryString) {
            const params = new URLSearchParams(queryString)
            const existingSfrm = params.get('sfrm')
            // Validate sfrm parameter - only accept valid image formats
            if (existingSfrm && validExtensions.includes(existingSfrm.toLowerCase())) {
                return existingSfrm.toLowerCase()
            }
        }
    }

    // Fall back to file extension
    const extension = url.split('.').pop()?.split('?')[0]?.toLowerCase()
    return validExtensions.includes(extension) ? extension : 'jpg'
}

const isDisUrl = (url) => {
    if (!url || typeof url !== 'string') return false
    return url.includes('/dw/image/v2/')
}

const convertToDisUrl = (url, sharedLibraryDis) => {
    // Validate input
    if (!url || typeof url !== 'string') return null

    // Already DIS URL
    if (isDisUrl(url)) return url

    // Try to parse image URL
    let urlObj
    try {
        urlObj = new URL(url)
    } catch {
        return null
    }

    // Check if it's a stokke domain
    if (!urlObj.hostname.includes('stokke.com')) return null

    // Check if URL is in convertible non-DIS format
    const nonDisPattern = /^\/on\/demandware\.static\/-\//
    if (!nonDisPattern.test(urlObj.pathname)) return null

    // URL is convertible - need sharedLibraryDis to convert
    if (!sharedLibraryDis || typeof sharedLibraryDis !== 'string') return null

    // Try to parse sharedLibraryDis URL
    let sharedLibraryUrl
    try {
        sharedLibraryUrl = new URL(sharedLibraryDis)
    } catch {
        return null
    }

    // Extract DIS base from sharedLibraryDis (everything up to /on/demandware.)
    const sharedLibraryPath = sharedLibraryUrl.pathname
    const demandwareIndex = sharedLibraryPath.indexOf('/on/demandware.')
    if (demandwareIndex === -1) return null

    const disBase = `${sharedLibraryUrl.protocol}//${
        sharedLibraryUrl.hostname
    }${sharedLibraryPath.substring(0, demandwareIndex)}/on/demandware.`

    // Extract the path after /on/demandware.static/-/
    const imagePath = urlObj.pathname.replace(/^\/on\/demandware\.static\/-\//, '')

    // Construct DIS URL: disBase + static/-/ + imagePath
    return `${disBase}static/-/${imagePath}`
}

export class DisService extends ImageService {
    /**
     * Create DIS service instance if URL is supported or convertible
     * @param {string} url - Image URL
     * @param {Object} context - Context with sharedLibraryStatic and sharedLibraryDis
     * @returns {DisService|null} Service instance or null
     */
    static create(url, context) {
        const {sharedLibraryStatic, sharedLibraryDis} = context

        // Resolve relative URLs
        const resolvedUrl = getSharedLibraryAssetUrl(url, sharedLibraryStatic)

        // Try to get or convert to DIS URL
        let disUrl = resolvedUrl
        let originalFormat = getOriginalFormat(resolvedUrl)
        let fallbackUrl = null

        if (!isDisUrl(resolvedUrl)) {
            disUrl = convertToDisUrl(resolvedUrl, sharedLibraryDis)
            if (!disUrl) {
                // Only log warning for Stokke URLs that should be convertible
                // External URLs are expected to not convert
                try {
                    const urlObj = new URL(resolvedUrl)
                    if (urlObj.hostname.includes('stokke.com')) {
                        logger.warn('DisService: URL conversion failed', resolvedUrl)
                    }
                } catch {
                    // Invalid URL, ignore
                }
                return null
            }
            // Preserve original format from pre-conversion URL
            originalFormat = getOriginalFormat(resolvedUrl)
            // Use original static URL as fallback in case DIS is down
            fallbackUrl = resolvedUrl
        }

        // Strip query parameters from baseUrl since they contain sizing/optimization params
        // that will be re-added by generateUrl()
        const baseUrl = disUrl.split('?')[0]

        const baseInfo = {
            baseUrl,
            originalFormat
        }

        // Pass the converted DIS URL as the service-ready URL
        // For convertible URLs, fallbackUrl stores the original static URL
        return new DisService(disUrl, baseInfo, fallbackUrl)
    }

    generateUrl(width, options = {}) {
        const {baseUrl, originalFormat} = this.baseInfo
        const {targetFormat = 'image/jpeg'} = options

        if (!baseUrl || !width || width <= 0) {
            if (!baseUrl) {
                logger.warn('DisService: Missing baseUrl')
            } else {
                logger.warn('DisService: Invalid width parameter', {width})
            }
            return null
        }

        const targetExt = FORMAT_EXTENSIONS[targetFormat] || 'jpg'

        // Replace file extension with target format
        const urlWithTargetExt = baseUrl.replace(
            /\.(gif|jp2|jpe?g|png|tiff?)(\?|$)/i,
            `.${targetExt}$2`
        )

        // Encode URL path
        const encodedUrl = encodeUrlPath(urlWithTargetExt)

        // Add DIS parameters
        return `${encodedUrl}?sfrm=${originalFormat}&sw=${width}&q=60`
    }

    getSupportedFormats() {
        const {originalFormat} = this.baseInfo
        const sourceSupportsTransparency = supportsTransparency(originalFormat)

        const allFormats = ['image/avif', 'image/webp', 'image/png', 'image/jpeg']

        if (sourceSupportsTransparency) {
            return allFormats.filter((format) => supportsTransparency(format))
        }

        return allFormats
    }

    generateSources(sizes) {
        const formats = this.getSupportedFormats()

        if (!sizes || sizes.length === 0) {
            // No sizes specified, generate single URL per format
            return formats.map((format) => ({
                type: format,
                srcSet: this.generateUrl(1200, {targetFormat: format})
            }))
        }

        // Generate sources for each format
        return formats.map((format) => {
            const srcsetEntries = sizes
                .map((width) => {
                    const url = this.generateUrl(width, {targetFormat: format})
                    return url ? `${url} ${width}w` : null
                })
                .filter(Boolean)

            return {
                type: format,
                srcSet: srcsetEntries.join(', ')
            }
        })
    }

    /**
     * Extract width from a DIS URL with sw parameter
     * @param {string} url - DIS image URL
     * @returns {number|null} Width in pixels or null if not found/invalid
     *
     * @example
     * service.extractWidthFromUrl('https://example.com/image.jpg?sw=800&q=60')
     * // Returns: 800
     */
    extractWidthFromUrl(url) {
        if (!url || typeof url !== 'string') return null

        // Extract sw parameter using regex
        const match = url.match(/[?&]sw=(\d+)/)
        if (!match) return null

        const width = parseInt(match[1], 10)
        return Number.isNaN(width) ? null : width
    }
}
